import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";
import type {
  InitiateTerminationBody,
  TerminationActionBody,
  TerminationCancelBody,
  TerminationDocumentUploadResult,
  TerminationFormContext,
  TerminationRequestDto,
  TerminationSubjectSeparationGateDtoV2,
} from "../../components/separation/Utils/types";

const PREFIX = "/v2/termination";

function unwrap<T>(raw: unknown): T {
  if (raw === null || raw === undefined) return raw as T;
  if (
    typeof raw === "object" &&
    raw !== null &&
    "data" in raw &&
    "success" in raw
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function normalizeList(raw: unknown): TerminationRequestDto[] {
  const inner = unwrap<TerminationRequestDto[] | null | undefined>(raw);
  if (inner == null) return [];
  return Array.isArray(inner) ? inner : [inner];
}

export const terminationApi = createApi({
  reducerPath: "terminationApi",
  baseQuery,
  tagTypes: [
    "TerminationForm",
    "TerminationList",
    "TerminationDetail",
    "TerminationSeparationGate",
  ],
  endpoints: (builder) => ({
    getTerminationFormContext: builder.query<TerminationFormContext, void>({
      query: () => `${PREFIX}/form-context`,
      transformResponse: (raw: unknown) =>
        unwrap<TerminationFormContext>(raw),
      providesTags: ["TerminationForm"],
    }),

    getTerminationSeparationGateForEmployee: builder.query<
      TerminationSubjectSeparationGateDtoV2,
      string
    >({
      query: (employeeId) =>
        `${PREFIX}/separation-gate/employee/${encodeURIComponent(employeeId)}`,
      transformResponse: (raw: unknown) =>
        unwrap<TerminationSubjectSeparationGateDtoV2>(raw),
      providesTags: (_r, _e, employeeId) => [
        { type: "TerminationSeparationGate" as const, id: employeeId },
      ],
    }),

    uploadTerminationSupportingDocument: builder.mutation<
      TerminationDocumentUploadResult,
      File
    >({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `${PREFIX}/upload-supporting-document`,
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (raw: unknown) => {
        const u = unwrap<
          TerminationDocumentUploadResult & { key?: string }
        >(raw);
        return {
          ...u,
          fileKey: u.fileKey ?? u.key,
        };
      },
    }),

    initiateTermination: builder.mutation<
      TerminationRequestDto,
      InitiateTerminationBody
    >({
      query: (body) => ({
        url: `${PREFIX}/initiate`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrap<TerminationRequestDto>(raw),
      invalidatesTags: (result, error, arg) => [
        "TerminationList",
        "TerminationForm",
        "TerminationDetail",
        { type: "TerminationSeparationGate" as const, id: arg.employeeId },
      ],
    }),

    listTerminations: builder.query<TerminationRequestDto[], void>({
      query: () => PREFIX,
      transformResponse: (raw: unknown) => normalizeList(raw),
      providesTags: ["TerminationList"],
    }),

    getTermination: builder.query<TerminationRequestDto, string>({
      query: (id) => `${PREFIX}/${id}`,
      transformResponse: (raw: unknown) =>
        unwrap<TerminationRequestDto>(raw),
      providesTags: (_r, _e, id) => [{ type: "TerminationDetail", id }],
    }),

    terminationAction: builder.mutation<
      TerminationRequestDto,
      { id: string; body: TerminationActionBody }
    >({
      query: ({ id, body }) => ({
        url: `${PREFIX}/${id}/action`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrap<TerminationRequestDto>(raw),
      invalidatesTags: ["TerminationList", "TerminationForm", "TerminationDetail"],
    }),

    cancelTermination: builder.mutation<
      TerminationRequestDto,
      { id: string; body: TerminationCancelBody }
    >({
      query: ({ id, body }) => ({
        url: `${PREFIX}/${id}/cancel`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: unknown) =>
        unwrap<TerminationRequestDto>(raw),
      invalidatesTags: ["TerminationList", "TerminationForm", "TerminationDetail"],
    }),
  }),
});

export const {
  useGetTerminationFormContextQuery,
  useGetTerminationSeparationGateForEmployeeQuery,
  useUploadTerminationSupportingDocumentMutation,
  useInitiateTerminationMutation,
  useListTerminationsQuery,
  useGetTerminationQuery,
  useLazyGetTerminationQuery,
  useTerminationActionMutation,
  useCancelTerminationMutation,
} = terminationApi;
